import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  FilterAccordionHeader,
  Icon,
  MultiColumnList,
  Loading,
} from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { useOkapiKy } from '@folio/stripes/core';

function ReportPermissions({ selectedReport }) {
  const [filterToggle, setFilterToggle] = useState(true);
  const [usersMap, setUsersMap] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const okapiKy = useOkapiKy();

  const permissions = selectedReport?.permissions || {};
  const visibility = permissions.visibility || 'public';
  const isPrivate = visibility === 'private';
  const isRestricted = visibility === 'restricted';

  // Fetch user display names
  useEffect(() => {
    if (!selectedReport?.permissions) return;
    const allIds = [
      ...(permissions.viewers || []),
      ...(permissions.editors || []),
      ...(permissions.executors || []),
      ...(permissions.owner ? [permissions.owner] : []),
    ].filter(Boolean);
    if (!allIds.length) return;

    setLoadingUsers(true);
    const cql = allIds.map((id) => `id=="${id}"`).join(' or ');
    okapiKy
      .get(`users?query=${encodeURIComponent(cql)}&limit=100`)
      .json()
      .then((data) => {
        const map = {};
        (data.users || []).forEach((u) => {
          map[u.id] = u;
        });
        setUsersMap(map);
      })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [selectedReport]);

  const getUserDisplayName = (userId) => {
    const user = usersMap[userId];
    if (!user) return userId;
    const { firstName, lastName } = user.personal || {};
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return user.username || userId;
  };

  const renderVisibilityInfo = () => {
    const styles = {
      public: {
        bg: '#e8f5e9',
        border: '#4caf50',
        icon: 'unlock',
        id: 'ui-reports.permissions.publicInfo',
      },
      private: {
        bg: '#fff3e0',
        border: '#ff9800',
        icon: 'lock',
        id: 'ui-reports.permissions.privateInfo',
      },
      restricted: {
        bg: '#e3f2fd',
        border: '#2196f3',
        icon: 'users',
        id: 'ui-reports.permissions.restrictedInfo',
      },
    };
    const s = styles[visibility] || styles.public;
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: s.bg,
          border: `1px solid ${s.border}`,
          borderRadius: '4px',
          marginBottom: '12px',
        }}
      >
        <Icon icon={s.icon} size="medium" />
        <span style={{ marginInlineStart: '8px' }}>
          <FormattedMessage id={s.id} defaultMessage={visibility} />
        </span>
      </div>
    );
  };

  // Build users table — owner first, then viewers/editors/executors
  const usersTableData = [
    ...(permissions.owner
      ? [{ id: permissions.owner, permission: 'owner' }]
      : []),
    ...(permissions.viewers || []).map((id) => ({ id, permission: 'view' })),
    ...(permissions.editors || []).map((id) => ({ id, permission: 'edit' })),
    ...(permissions.executors || []).map((id) => ({
      id,
      permission: 'execute',
    })),
  ];

  return (
    <Accordion
      id="report-details-permissions-accordion"
      label={
        <FormattedMessage
          id="ui-reports.newReport.saveReportPane.permissions.accordion.header"
          defaultMessage="User Permissions"
        />
      }
      onToggle={() => setFilterToggle(!filterToggle)}
      open={filterToggle}
      separator
      header={FilterAccordionHeader}
    >
      {renderVisibilityInfo()}

      {/* Owner + authorized users in one table */}
      {(isPrivate || isRestricted) &&
        (loadingUsers ? (
          <Loading />
        ) : usersTableData.length > 0 ? (
          <MultiColumnList
            id="report-permissions-users-list"
            interactive={false}
            visibleColumns={['name', 'permission']}
            contentData={usersTableData.map((u) => ({
              name: getUserDisplayName(u.id),
              permission: u.permission === 'owner'
                ? <FormattedMessage id="ui-reports.permissions.owner" defaultMessage="Owner" />
                : <FormattedMessage id={`ui-reports.permissions.${u.permission}Users`} defaultMessage={u.permission} />,
            }))}
            columnMapping={{
              name: (
                <FormattedMessage
                  id="ui-reports.permissions.userName"
                  defaultMessage="User"
                />
              ),
              permission: (
                <FormattedMessage
                  id="ui-reports.permissions.type"
                  defaultMessage="Permission"
                />
              ),
            }}
            columnWidths={{ name: '70%', permission: '30%' }}
          />
        ) : (
          <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
            <FormattedMessage
              id="ui-reports.newReport.saveReportPane.permissions.noUsers"
              defaultMessage="No authorized users"
            />
          </div>
        ))}
    </Accordion>
  );
}

ReportPermissions.propTypes = {
  selectedReport: PropTypes.object,
  onUpdate: PropTypes.func,
};

export default ReportPermissions;
